import { useEffect, useMemo, useState, MouseEvent, useCallback, RefObject } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useFuzzySearchList } from '@nozbe/microfuzz/react';
import { useInteractions } from '@floating-ui/react';

import { useExplorationsSearchQuery } from '@/graphql';
import { useBuildAccountUrl, useSelectedAccount } from '@/lib/accounts/context';
import { useTrackEvent } from '@/lib/analytics';
import { useIsScrolled } from '@/lib/hooks/use-is-scrolled';
import { isReplicableCell } from '@/core/cell';
import {
  buildExplorationFromCells,
  getCleanedExplorationName,
  getExplorationType,
  isModelExploration,
} from '@/core/exploration';

import { notNil } from '@/lib/utils';

import { Icon } from '../../components/icon';
import { Loader } from '../../components/loader';
import { Exploration, Metric, Model } from '../types';
import { ExplorationList } from './exploration-list';
import { convertExplorationsArrayTypes } from '../input';
import { isModel } from './utils';
import {
  buildExplorationHashUrl,
  buildExplorationUrl,
  getCellBaseModel,
  getUnparameterizedExplorations,
} from '../utils';
import { extractCellFromExploration } from '../exploration/utils';
import { flattenLists } from '.';

import styles from './exploration-search.module.scss';

interface SearchResultProps {
  explorations: Exploration[];
  models: Model[];
  metrics: Metric[];
  searchTerm: string;
  /**
   * When set to true, the search results will allow selecting individual cells, full exploration otherwise.
   */
  selectIndividualCells: boolean;
  onClickExploration?: (exploration: Exploration) => boolean | undefined;
  onClickSearchResultRef?: RefObject<(() => void) | null>;
  activeIndex: number | null;
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
}

export const SearchResults = (props: SearchResultProps) => {
  const {
    explorations,
    searchTerm,
    onClickExploration,
    selectIndividualCells,
    activeIndex,
    getItemProps,
  } = props;

  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const [isScrolled, scrollContainerRef] = useIsScrolled<HTMLDivElement>();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const account = useSelectedAccount();
  const buildAccountUrl = useBuildAccountUrl();

  const toggleItemExpand = (id: string) => {
    setExpandedItems((expandedItems) => {
      const set = new Set(expandedItems);
      set.has(id) ? set.delete(id) : set.add(id);
      return set;
    });
  };

  // Avoid fetching on first letter by using an empty initial value
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const {
    data,
    loading: queryLoading,
    error,
  } = useExplorationsSearchQuery({
    variables: { accountId: account.accountId, search: debouncedSearchTerm },
    skip: debouncedSearchTerm.length === 0,
  });

  const loading = queryLoading || debouncedSearchTerm !== searchTerm;

  const instantResults = useFuzzySearchList<Exploration, Exploration>({
    strategy: 'smart',
    list: explorations,
    queryText: searchTerm.trim(),
    getText: useCallback((option: Exploration) => [getCleanedExplorationName(option.name)], []),
    mapResultItem: useCallback(({ item }) => item, []),
  });

  const semanticResults = useMemo(
    () =>
      getUnparameterizedExplorations(
        convertExplorationsArrayTypes(data?.account?.explorationSearch ?? []),
      ).filter(
        (e) =>
          !instantResults.some(
            (e2) =>
              e.explorationId === e2.explorationId ||
              (getExplorationType(e) === 'model' &&
                getExplorationType(e2) === 'model' &&
                e.view.cells.length > 0 &&
                e2.view.cells.length > 0 &&
                getCellBaseModel(e.view.cells[0], e) === getCellBaseModel(e2.view.cells[0], e2)),
          ),
      ),
    [data, instantResults],
  );

  const buildItem = useCallback(
    (exploration: Exploration) => {
      const getUrl = () => buildAccountUrl(buildExplorationUrl(exploration));
      return {
        exploration,
        getUrl,
        onClick: (event?: MouseEvent) => {
          event?.preventDefault();
          if (selectIndividualCells && !isModelExploration(exploration)) {
            toggleItemExpand(exploration.explorationId);
            return;
          }
          if (onClickExploration === undefined || onClickExploration(exploration) !== false) {
            navigate(getUrl());
          }
        },
        children: exploration.view.cells
          .filter(() => !isModelExploration(exploration))
          .filter(() => selectIndividualCells)
          .filter(isReplicableCell)
          .map((cell) => {
            try {
              const { cell: extractedCell, dependencies } = extractCellFromExploration(
                cell.id,
                exploration,
              );
              const outputExploration = buildExplorationFromCells([extractedCell, ...dependencies]);
              const getUrl = () => buildAccountUrl(buildExplorationHashUrl(outputExploration));
              return {
                cell,
                getUrl,
                onClick: (event?: MouseEvent) => {
                  event?.preventDefault();
                  if (
                    onClickExploration === undefined ||
                    onClickExploration(outputExploration) !== false
                  ) {
                    navigate(getUrl());
                  }
                },
              };
            } catch {
              // Ignore cells we're unable to extract, e.g. with invalid pipelines
              return null;
            }
          })
          .filter(notNil),
      };
    },
    [buildAccountUrl, navigate, onClickExploration, selectIndividualCells],
  );

  const instantResultsList = useMemo(
    () => instantResults.map(buildItem),
    [buildItem, instantResults],
  );
  const semanticResultsList = useMemo(
    () => semanticResults.map(buildItem),
    [buildItem, semanticResults],
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      const modelCount = instantResults.filter((e) => isModel(e.labels)).length;
      trackEvent('Exploration Search', {
        searchTerm,
        instantCount: instantResults.length,
        modelCount,
        explorationCount: instantResults.length - modelCount,
      });
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (loading || data === undefined) {
      return;
    }
    const modelCount = semanticResults.filter((e) => isModel(e.labels)).length;
    trackEvent('Exploration Search Results', {
      searchTerm: debouncedSearchTerm,
      instantCount: instantResults.length,
      resultCount: semanticResults.length,
      modelCount,
      explorationCount: semanticResults.length - modelCount,
    });
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.account?.explorationSearch]);

  const handleItemClick = useCallback(() => {
    const flattenedItems = flattenLists([
      { children: instantResultsList },
      { children: semanticResultsList },
    ]);
    const item = flattenedItems.at(activeIndex !== null && activeIndex !== -1 ? activeIndex : 0);
    if (item !== undefined) {
      item.onClick();
    }
  }, [activeIndex, instantResultsList, semanticResultsList]);

  useEffect(() => {
    if (props.onClickSearchResultRef) {
      props.onClickSearchResultRef.current = handleItemClick;
    }
  }, [handleItemClick, props.onClickSearchResultRef]);

  return (
    <>
      <div
        className={classNames(styles.searchResults, { [styles.scrolled]: isScrolled })}
        ref={scrollContainerRef}>
        {instantResultsList.length > 0 && (
          <ExplorationList
            items={instantResultsList}
            activeIndex={activeIndex}
            getItemProps={getItemProps}
            expandedItems={expandedItems}
          />
        )}
        {loading && (
          <div className={styles.loader}>
            <Loader />
          </div>
        )}
        {!loading && semanticResultsList.length > 0 && (
          <ExplorationList
            items={semanticResultsList}
            activeIndex={activeIndex}
            getItemProps={getItemProps}
            expandedItems={expandedItems}
          />
        )}
        {!loading && error && (
          <div className={styles.error}>
            <Icon name="CloudDrizzle" size={16} />
            Semantic search failed. Only exact matches are displayed.
          </div>
        )}
      </div>
    </>
  );
};
